import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useSubjectId } from '@healthblocks-io/core'
import { t } from '@lingui/macro'
import { isEmpty, isNil } from 'ramda'
import { useEffect, useMemo } from 'react'
import { useTheme } from 'styled-components'

import { Flex, Padding } from '@/@bothrs-ui/web/components/layout'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { formatInTZ } from '@/modules/shared/helpers/locale'
import { Info } from '../../../appointment/components/info/Info'
import { useEncounters } from '../../hooks/useEncounters'
import { StContainer } from './EncounterTimeInfo.styled'

import type { Encounter } from '../../types/encounter.types'

type EncounterStatus = 'planned' | 'timeProposed' | 'scheduled' | 'completed'

type Props = {
  encounter?: Encounter
}

const EncounterTimeInfo = ({ encounter }: Props) => {
  const theme = useTheme()
  const subjectId = useSubjectId()

  const {
    confirmEncounterTime: {
      execute: confirmEncounterTime,
      value: confirmEncounterTimeValue,
      isLoading: confirmEncounterTimeLoading,
      reset: resetConfirmEncounterTimeValue,
    },
  } = useEncounters()

  // Template variables
  // ------------------------------------------------------------------------- /
  const hasValidConfirmation = useMemo(
    () =>
      !encounter?.meta?.requiresConfirmation &&
      !isNil(encounter?.meta?.confirmedDates) &&
      !isEmpty(encounter?.meta?.confirmedDates),
    [encounter],
  )

  const status: EncounterStatus = useMemo(() => {
    if (!isNil(encounter?.end) && !isEmpty(encounter?.end)) {
      return 'completed'
    }
    if (hasValidConfirmation && encounter?.start) {
      return 'scheduled'
    }
    if (encounter?.meta.requiresConfirmation) {
      return 'timeProposed'
    }
    if (!isEmpty(encounter?.trigger) && encounter?.trigger.startsWith('operatie_')) {
      return 'scheduled'
    }
    if (!isEmpty(encounter?.trigger) && encounter?.trigger.startsWith('opname_')) {
      return 'planned'
    }
    return 'planned'
  }, [encounter?.trigger, encounter?.start, encounter?.end, encounter?.meta.requiresConfirmation, hasValidConfirmation])

  const timeMessage = useMemo(() => {
    switch (status) {
      case 'planned': {
        return formatInTZ(new Date(encounter?.start as string), 'HH:mm')
      }
      case 'scheduled': {
        return t({
          id: 'encounters.time-unconfirmed',
          message:
            'Ten laatste twee dagen voor je afspraak verneem je hoe laat we je ter plaatse verwachten, tenzij reeds meegedeeld op moment van vastleggen afspraak/opname',
        })
      }
      case 'timeProposed': {
        return `${t({
          id: 'encounters.check-in',
          message: 'Aanmelden',
        })}: ${formatInTZ(new Date(encounter?.start as string), 'HH:mm')}`
      }
      case 'completed': {
        return formatInTZ(new Date(encounter?.start as string), 'HH:mm')
      }
      default: {
        return ''
      }
    }
  }, [encounter?.start, status])

  // Effects
  // ------------------------------------------------------------------------- /
  useEffect(() => {
    if (encounter?.id && confirmEncounterTimeValue) {
      resetConfirmEncounterTimeValue()
    }
  }, [encounter?.id])

  // Interaction Methods
  // ------------------------------------------------------------------------- /
  function onConfirmTime() {
    if (encounter && subjectId) {
      confirmEncounterTime({
        synopsEncounterId: encounter.id.toString(),
        synopsPatientId: subjectId,
      })
    }
  }

  if (!encounter) {
    return null
  }

  return (
    <StContainer backdropEnabled={status !== 'completed'}>
      <Info
        icon={['fal', 'clock']}
        color={
          status === 'timeProposed'
            ? theme.Colors.Status.Error
            : status === 'completed'
              ? theme.Colors.Neutrals.Dark
              : theme.Colors.Category.Appointment
        }
        info={[timeMessage]}
        bold={encounter?.meta?.requiresConfirmation}
      />

      {encounter?.meta.requiresConfirmation ? (
        <Button
          style={{ maxWidth: '150px' }}
          variant='Primary'
          isLoading={confirmEncounterTimeLoading}
          disabled={confirmEncounterTimeValue}
          inline
          onClick={onConfirmTime}
        >
          <Flex alignItems='center'>
            <FontAwesomeIcon icon={['fal', 'check']} />
            {confirmEncounterTimeValue ? null : (
              <Padding pl={theme.UI.Spacing.Space2}>
                <Span type='BodyMediumRegular'>
                  {t({
                    id: 'encounters.time-confirm-cta',
                    message: 'Bevestig je aanwezigheid',
                  })}
                </Span>
              </Padding>
            )}
          </Flex>
        </Button>
      ) : null}
    </StContainer>
  )
}

export { EncounterTimeInfo }
